import { Component } from '@theme/component';

/** @typedef {typeof globalThis} Window */

/**
 * @typedef {Object} Refs
 * @property {HTMLElement} [text]
 *
 * @extends {Component<Refs>}
 */
class ProductTitle extends Component {
  constructor() {
    super();
    this.originalTitle = '';
    this.boundHandleResize = this.#handleResize.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement} */
    const textElement = this.refs.text || this.querySelector('.title-text') || this;
    this.originalTitle = textElement.textContent?.trim() || '';

    this.#initializeTruncation();
    this.#initializeVariantButtons();
  }

  /**
   * Listen for Retail / Cashpack clicks
   */
  #initializeVariantButtons() {
    const retailBtn = document.querySelector('[data-title-type="retail"]');
    const cashpackBtn = document.querySelector('[data-title-type="cashpack"]');

    retailBtn?.addEventListener('click', () => {
      this.updateTitle('Retail Title');
    });

    cashpackBtn?.addEventListener('click', () => {
      this.updateTitle('Sitno Cash Pack with 6 Units');
    });
  }

  /**
   * Update product title text
   * @param {string} newTitle
   */
  updateTitle(newTitle) {
    /** @type {HTMLElement} */
    const textElement = this.refs.text || this.querySelector('.title-text') || this;
    textElement.textContent = newTitle;
    this.#calculateTruncation();
  }

  /**
   * Initialize the title truncation
   */
  #initializeTruncation() {
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        this.#calculateTruncation();
      });

      this.resizeObserver.observe(this);
      this.#calculateTruncation();
    } else {
      /** @type {Window} */
      window.addEventListener('resize', this.boundHandleResize);
      this.#calculateTruncation();
    }
  }

  /**
   * Calculate truncation for the title
   */
  #calculateTruncation() {
    /** @type {HTMLElement} */
    const textElement = this.refs.text || this.querySelector('.title-text') || this;
    if (!textElement.textContent) return;

    const containerHeight = this.clientHeight;
    const computedStyle = window.getComputedStyle(this);

    let lineHeight = parseFloat(computedStyle.lineHeight);
    if (Number.isNaN(lineHeight)) {
      lineHeight = parseFloat(window.getComputedStyle(textElement).fontSize) * 1.2;
    }

    const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;

    const availableHeight = containerHeight - paddingTop - paddingBottom;
    const maxLines = Math.max(1, Math.floor(availableHeight / lineHeight));

    textElement.style.display = '-webkit-box';
    textElement.style.webkitBoxOrient = 'vertical';
    textElement.style.overflow = 'hidden';
    textElement.style.textOverflow = 'ellipsis';
    textElement.style.webkitLineClamp = String(maxLines);
  }

  /**
   * Handle window resize events
   */
  #handleResize() {
    this.#calculateTruncation();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    window.removeEventListener('resize', this.boundHandleResize);
  }
}

if (!customElements.get('product-title')) {
  customElements.define('product-title', ProductTitle);
}

export default ProductTitle;